import {
  type DomainDirection,
  type DomainIntention,
  type DomainPriority,
  type EvidenceKind,
  FITNESS_DOMAINS,
  type FitnessDomain,
} from './fitnessDomain'

/**
 * The "overall fitness" preset, and what each domain collects (D-090).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A PRESET PROPOSES. IT NEVER ENROLS.
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `'overallFitness'` is **not a stored value and not a domain.** It is a
 *  shortcut for someone who cannot answer "which areas?" — it proposes a set, the
 *  user reviews it on the next step, and what is persisted is always the set of
 *  individual domains they confirmed. Storing the preset itself would make
 *  "overall fitness" an unmeasurable goal, which is the thing this design exists
 *  to avoid.
 *
 *  This is D-020's rule applied one level further up than the project: producing
 *  a proposal changes nothing, and only an explicit confirmation creates records.
 *
 *  Nothing here is a coaching threshold. There is no number in this file, and the
 *  proposal is an ordering of priorities rather than a claim about what anyone
 *  should do — which is why it needs no entry in `rules/thresholds.ts` and no
 *  provenance beyond D-090.
 */

/** Everything the setup wizard offers as a starting point. */
export const DOMAIN_SELECTION_PRESETS = ['overallFitness'] as const

export type DomainSelectionPreset = (typeof DOMAIN_SELECTION_PRESETS)[number]

export const DOMAIN_SELECTION_PRESET_LABELS: Readonly<Record<DomainSelectionPreset, string>> = {
  overallFitness: 'Overall fitness',
}

export interface ProposedDomain {
  readonly domain: FitnessDomain
  readonly intention: DomainIntention
  readonly priority: DomainPriority
}

/**
 * What "overall fitness" proposes.
 *
 * **One primary, one supporting, and two protected.** That shape is the
 * recommended configuration rather than a limit: a beginner asked to improve four
 * things at once improves none of them, and the two protections are what stop a
 * fat-loss project quietly costing strength and mobility.
 *
 * Body composition leads because it is the only domain with a deterministic
 * engine behind it — the app can actually judge a week of it — and cardio
 * supports because it is the one the wearable can evidence without any new manual
 * entry. **This ordering is a proposal the user overrides on the next step**, and
 * the wizard shows it as such.
 */
export const OVERALL_FITNESS_PROPOSAL: readonly ProposedDomain[] = [
  { domain: 'bodyComposition', intention: 'improve', priority: 'primary' },
  { domain: 'cardio', intention: 'improve', priority: 'supporting' },
  { domain: 'strength', intention: 'maintain', priority: 'protect' },
  { domain: 'mobility', intention: 'maintain', priority: 'protect' },
]

export function proposalFor(preset: DomainSelectionPreset): readonly ProposedDomain[] {
  /* A total map with one entry today. A `switch` with a default would let a
     second preset be added and silently return the first one's proposal. */
  const proposals: Readonly<Record<DomainSelectionPreset, readonly ProposedDomain[]>> = {
    overallFitness: OVERALL_FITNESS_PROPOSAL,
  }
  return proposals[preset]
}

/**
 * What each domain collects, when the user is not told otherwise.
 *
 * **Declaring an evidence kind is not the same as a rule reading it** (D-060).
 * This is the collection half only: cardio collects sessions and, later, wearable
 * summaries, and nothing in `rules/` judges them yet. The domain registry is what
 * says whether a domain can be judged.
 *
 * Recovery is deliberately in no list. It is a cross-cutting guardrail that
 * applies whatever is active, so attaching it to a domain would make it something
 * the user could switch off by pausing a goal.
 */
export const DEFAULT_EVIDENCE_KINDS: Readonly<Record<FitnessDomain, readonly EvidenceKind[]>> = {
  bodyComposition: ['weighIn', 'circumference', 'photo'],
  cardio: ['cardioSession', 'wearableDailySummary'],
  strength: ['strengthSet'],
  mobility: ['mobilitySession'],
}

/**
 * The direction an intention implies, before the user says otherwise.
 *
 * `restore` is never inferred. Returning to a previous level after an injury or a
 * break is a claim about the user's history that the app has no way to know, so it
 * is offered as a choice and never guessed at.
 */
export function defaultDirectionFor(intention: DomainIntention): DomainDirection {
  return intention === 'improve' ? 'improve' : 'maintain'
}

/**
 * Whether this domain's evidence needs the body-composition measurement flow.
 *
 * The single place this question is answered, so the wizard's step list and the
 * setup transaction cannot disagree about it — which is exactly how a cardio-only
 * user ends up being asked for a waist measurement by one of them.
 */
export function needsBodyMeasurement(domains: readonly FitnessDomain[]): boolean {
  return domains.includes('bodyComposition')
}

/** Whether any active domain makes the training questions worth asking. */
export function needsTrainingContext(domains: readonly FitnessDomain[]): boolean {
  return (
    domains.includes('bodyComposition') ||
    domains.includes('strength') ||
    domains.includes('cardio')
  )
}

/**
 * Order a selection the way every screen shows it.
 *
 * Reads `FITNESS_DOMAINS`, so the declaration order is the display order in one
 * place rather than in each screen that lists them.
 */
export function orderDomains(domains: readonly FitnessDomain[]): readonly FitnessDomain[] {
  return FITNESS_DOMAINS.filter((domain) => domains.includes(domain))
}
